import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonProps, CSSObject, IconButton, IconButtonProps, styled } from "@mui/material";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import { AavoTooltip } from "../tooltips/AavoTooltip";

export interface AavoIconButtonProps extends IconButtonProps {
	variant?: ButtonProps["variant"];
	icon: IconDefinition;
	loading?: boolean;
	tooltip?: string;
}

export const AavoIconButton = ({ icon, loading, tooltip, ...other }: AavoIconButtonProps) => {
	return (
		<AavoTooltip title={tooltip}>
			<StyledIconButton {...other}>
				{loading ? (
					<FontAwesomeIcon icon={faSpinnerThird} spin={true} />
				) : (
					<FontAwesomeIcon icon={icon} className={"fa-fw"} />
				)}
			</StyledIconButton>
		</AavoTooltip>
	);
};

const StyledIconButton = styled(IconButton)<{
	variant?: ButtonProps["variant"];
}>(({ theme, variant, color, disabled }) => {
	const overrides: CSSObject = {};
	overrides.borderRadius = theme.spacing(1);

	const colorAsVariant =
		color === undefined || color === "inherit" || color === "default" ? "primary" : color;
	if (variant === "contained") {
		if (disabled) {
			overrides["&:disabled"] = {
				backgroundColor: theme.palette.action.disabled,
			};
		}
		overrides[":hover"] = {
			backgroundColor: getHoverColorFromHex(theme.palette[colorAsVariant].main),
		};
		overrides.backgroundColor = theme.palette[colorAsVariant].main;
		overrides.color = theme.palette[colorAsVariant].contrastText;
	}
	if (variant === "outlined") {
		overrides.outline = `1px solid ${
			disabled ? theme.palette.action.disabled : theme.palette[colorAsVariant].main
		}`;
		overrides.outlineOffset = "-1px";
		overrides.color = theme.palette[colorAsVariant].main;
	}

	return {
		...overrides,
	};
});

function hexToRgb(hex: any) {
	// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	hex = hex.replace(shorthandRegex, function (_: any, r: any, g: any, b: any) {
		return r + r + g + g + b + b;
	});

	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1]!, 16),
				g: parseInt(result[2]!, 16),
				b: parseInt(result[3]!, 16),
			}
		: null;
}

function getHoverColorFromHex(hex: any) {
	const rgb = hexToRgb(hex);
	if (rgb) {
		return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`;
	}
	return hex;
}
